"use client";

import { useState, useEffect } from "react";
import api, { mediaUrl } from "@/lib/api";

export interface Associate {
  id_associado: string;
  nome_associado: string;
  imagem1_associado: string | null;
  imagem2_associado: string | null;
  whatsapp_associado?: string;
  website?: {
    title?: string;
    url?: string;
  };
  facebook?: {
    title?: string;
    url?: string;
  };
  instagram_associado?: string;
  location?: string;
  description?: string;
  additionalInfo?: string;
  images: string[];
  products: string[];
}

export interface AssociateSimple {
  id_associado: string;
  nome_associado: string;
}

export function useAssociates() {
  const [associates, setAssociates] = useState<AssociateSimple[]>([]);

  const fetchAssociates = async () => {
    try {
      const response = await api.get("/associados/");
      const data = response.data;

      const formattedAssociates: AssociateSimple[] = data.map((assoc: { id_associado: number; nome_associado: string }) => ({
        id_associado: assoc.id_associado.toString(),
        nome_associado: assoc.nome_associado,
      }));

      setAssociates(formattedAssociates);
    } catch (error) {
      console.error("Error fetching associates:", error);
    }
  };

  const fetchAssociateInfo = async (idAssociado: string | undefined) => {
    try {
      const response = await api.get(`/associados/${idAssociado}/`);
      const data = response.data;

      const formattedAssociate: Associate = {
        id_associado: data.id_associado.toString(),
        nome_associado: data.nome_associado,
        imagem1_associado: mediaUrl(data.foto1_associado) || null,
        imagem2_associado: mediaUrl(data.foto2_associado) || null,
        whatsapp_associado: data.whatsapp_associado || undefined,
        website: data.siteurl_associado
          ? { title: data.sitetitulo_associado || undefined, url: data.siteurl_associado }
          : undefined,
        facebook: data.facebookurl_associado
          ? { title: data.facebooktitulo_associado || undefined, url: data.facebookurl_associado }
          : undefined,
        instagram_associado: data.instagram_associado || undefined,
        location: data.local_associado || undefined,
        description: data.text01_associado || undefined,
        additionalInfo: data.text02_associado || undefined,
        images: [
          data.foto1_associado,
          data.foto2_associado,
          data.foto3_associado,
          data.foto4_associado,
          data.foto5_associado,
          data.foto6_associado,
        ].filter(Boolean).map(mediaUrl),
        products: data.produtor ? [data.produtor] : [],
      };

      return formattedAssociate;
    } catch (error) {
      console.error("Error fetching associate:", error);
    }
  };

  useEffect(() => {
    fetchAssociates();
  }, []);

  return { associates, fetchAssociates, fetchAssociateInfo };
}

// Server-side fetch function for SSR
export async function fetchAssociateServer(id: string): Promise<Associate | null> {
  try {
    const baseUrl = process.env.API_URL || process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";
    const res = await fetch(`${baseUrl}/associados/${id}/`, {
      cache: "no-store",
    });
    if (!res.ok) return null;
    const data = await res.json();

    return {
      id_associado: data.id_associado.toString(),
      nome_associado: data.nome_associado,
      imagem1_associado: mediaUrl(data.foto1_associado) || null,
      imagem2_associado: mediaUrl(data.foto2_associado) || null,
      whatsapp_associado: data.whatsapp_associado || undefined,
      website: data.siteurl_associado
        ? { title: data.sitetitulo_associado || undefined, url: data.siteurl_associado }
        : undefined,
      facebook: data.facebookurl_associado
        ? { title: data.facebooktitulo_associado || undefined, url: data.facebookurl_associado }
        : undefined,
      instagram_associado: data.instagram_associado || undefined,
      location: data.local_associado || undefined,
      description: data.text01_associado || undefined,
      additionalInfo: data.text02_associado || undefined,
      images: [
        data.foto1_associado,
        data.foto2_associado,
        data.foto3_associado,
        data.foto4_associado,
        data.foto5_associado,
        data.foto6_associado,
      ].filter(Boolean).map(mediaUrl),
      products: data.produtor ? [data.produtor] : [],
    };
  } catch (error) {
    console.error("Error fetching associate server-side:", error);
    return null;
  }
}
